/* eslint-disable react/prop-types */
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme, alpha } from "@mui/material/styles";

function SimopsDialog({ open, onClose, simopsData, onPermitClick, t, isXs }) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const locale = i18n.language || "en";

  // Group permits by site and then by day
  const groupedBySiteAndDay = useMemo(() => {
    const result = {};

    Object.entries(simopsData).forEach(([siteName, permits]) => {
      const dayGroups = {};

      permits.forEach((permit) => {
        const date = new Date(permit.start_timestamp);
        const dayKey = date.toLocaleDateString(locale, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        if (!dayGroups[dayKey]) {
          dayGroups[dayKey] = {
            permits: [],
            firstTimestamp: permit.start_timestamp,
          };
        }
        dayGroups[dayKey].permits.push(permit);
      });

      // Sort permits within each day by time
      Object.keys(dayGroups).forEach((dayKey) => {
        dayGroups[dayKey].permits.sort(
          (a, b) => new Date(a.start_timestamp) - new Date(b.start_timestamp)
        );
      });

      // Convert to sorted array of [dayKey, permits] pairs sorted by date
      const sortedDays = Object.entries(dayGroups)
        .sort(
          (a, b) =>
            new Date(a[1].firstTimestamp) - new Date(b[1].firstTimestamp)
        )
        .map(([dayKey, data]) => [dayKey, data.permits]);

      result[siteName] = sortedDays;
    });

    return result;
  }, [simopsData, locale]);

  const formatTime = useCallback(
    (timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    [locale]
  );

  // Calculate total SIMOPS permits (only sites with 2+ permits)
  const totalSimopsPermits = useMemo(() => {
    return Object.values(groupedBySiteAndDay).reduce((total, dayGroups) => {
      const siteTotal = dayGroups.reduce(
        (sum, [, permits]) => sum + permits.length,
        0
      );
      return siteTotal >= 2 ? total + siteTotal : total;
    }, 0);
  }, [groupedBySiteAndDay]);

  // Check if there are any valid SIMOPS sites
  const hasSimops = useMemo(() => {
    return Object.values(groupedBySiteAndDay).some((dayGroups) => {
      const siteTotal = dayGroups.reduce(
        (sum, [, permits]) => sum + permits.length,
        0
      );
      return siteTotal >= 2;
    });
  }, [groupedBySiteAndDay]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isXs}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[24],
          background: `linear-gradient(to bottom, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
          color: theme.palette.warning.contrastText || "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "200px",
            height: "200px",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.common.white,
              0.1
            )} 0%, transparent 70%)`,
            borderRadius: "50%",
            transform: "translate(50%, -50%)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.common.white, 0.2),
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                ⚠️
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  mb: 0.5,
                }}
              >
                {t("documentsPage.simopsDialogTitle") ||
                  "SIMOPS - Simultaneous Operations"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                }}
              >
                {t("documentsPage.simopsSubtitle") ||
                  "Monitor overlapping operations across sites"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={totalSimopsPermits}
              sx={{
                height: 32,
                fontSize: "1rem",
                fontWeight: 700,
                bgcolor: alpha(theme.palette.common.white, 0.25),
                color: theme.palette.warning.contrastText || "#fff",
                backdropFilter: "blur(10px)",
                border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
              }}
            />
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: theme.palette.warning.contrastText || "#fff",
                bgcolor: alpha(theme.palette.common.white, 0.15),
                backdropFilter: "blur(10px)",
                "&:hover": {
                  bgcolor: alpha(theme.palette.common.white, 0.25),
                  transform: "rotate(90deg)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {!hasSimops ? (
          <Box
            sx={{
              p: 8,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.info.main, 0.1),
                mb: 2,
              }}
            >
              <Typography variant="h3">✓</Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              {t("documentsPage.noSimopsTitle") || "All Clear"}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 500 }}
            >
              {t("documentsPage.noSimopsFound") ||
                "No simultaneous operations found. Sites must have 2 or more active permits to be considered SIMOPS."}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {Object.entries(groupedBySiteAndDay)
              .filter(([, dayGroups]) => {
                // Only show sites with 2 or more permits (SIMOPS requires multiple operations)
                const totalSitePermits = dayGroups.reduce(
                  (sum, [, permits]) => sum + permits.length,
                  0
                );
                return totalSitePermits >= 2;
              })
              .map(([siteName, dayGroups], siteIndex) => {
                const totalSitePermits = dayGroups.reduce(
                  (sum, [, permits]) => sum + permits.length,
                  0
                );

                return (
                  <Box key={siteName}>
                    {/* Site Header */}
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.warning.main,
                          0.15
                        )} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                        p: 3,
                        borderLeft: `4px solid ${theme.palette.warning.main}`,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Background Decoration */}
                      <Box
                        sx={{
                          position: "absolute",
                          right: -20,
                          top: -20,
                          width: 100,
                          height: 100,
                          borderRadius: "50%",
                          bgcolor: alpha(theme.palette.warning.main, 0.05),
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: theme.palette.warning.main,
                              color:
                                theme.palette.warning.contrastText || "#fff",
                              fontWeight: 700,
                              fontSize: "1.25rem",
                              boxShadow: theme.shadows[2],
                            }}
                          >
                            📍
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                              }}
                            >
                              {siteName}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                              }}
                            >
                              {t("documentsPage.potentialSimops", {
                                count: totalSitePermits,
                              }) || `Potential SIMOPS (${totalSitePermits})`}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Day Groups */}
                    {dayGroups.map(([dayKey, permits]) => (
                      <Box key={dayKey}>
                        {/* Day Header */}
                        <Box
                          sx={{
                            bgcolor: theme.palette.background.paper,
                            p: 2,
                            px: 3,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              fontSize: "0.875rem",
                            }}
                          >
                            📅
                          </Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              flex: 1,
                            }}
                          >
                            {dayKey}
                          </Typography>
                          <Chip
                            label={`${permits.length} ${
                              permits.length === 1
                                ? t("documentsPage.permit") || "permit"
                                : t("documentsPage.permits") || "permits"
                            }`}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                        </Box>

                        {/* Permits List */}
                        {permits.map((permit, permitIndex) => (
                          <Box key={permit.id}>
                            <ListItem
                              disablePadding
                              sx={{
                                bgcolor: theme.palette.background.paper,
                              }}
                            >
                              <ListItemButton
                                onClick={() => onPermitClick(permit.id)}
                                sx={{
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  py: 3,
                                  px: 3,
                                  m: 2,
                                  borderRadius: 2,
                                  border: `1px solid ${theme.palette.divider}`,
                                  bgcolor: theme.palette.background.default,
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    bgcolor: theme.palette.background.paper,
                                    borderColor: theme.palette.primary.main,
                                    transform: "translateY(-2px)",
                                    boxShadow: theme.shadows[4],
                                  },
                                }}
                              >
                                <Box sx={{ width: "100%" }}>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("documentsPage.jobStartTimeLabel") ||
                                        "Job start time"}
                                      :
                                    </Box>{" "}
                                    {formatTime(permit.start_timestamp)}
                                  </Typography>

                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("documentsPage.workflowLabel") ||
                                        "Workflow"}
                                      :
                                    </Box>{" "}
                                    {permit.workflow?.name || "N/A"}
                                  </Typography>

                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("documentsPage.titleLabel") || "Title"}
                                      :
                                    </Box>{" "}
                                    {permit.title}
                                  </Typography>

                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("documentsPage.jobDescriptionLabel") ||
                                        "Job Description"}
                                      :
                                    </Box>{" "}
                                    {permit.description}
                                  </Typography>

                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("documentsPage.createdByLabel") ||
                                        "Created by"}
                                      :
                                    </Box>{" "}
                                    {permit.author?.first_name}{" "}
                                    {permit.author?.last_name}
                                  </Typography>

                                  <Typography variant="body2">
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("documentsPage.statusLabel") ||
                                        "Status"}
                                      :
                                    </Box>{" "}
                                    {permit.history_steps?.[
                                      permit.history_steps.length - 1
                                    ]?.status || "N/A"}
                                  </Typography>
                                </Box>
                              </ListItemButton>
                            </ListItem>
                            {permitIndex < permits.length - 1 && <Divider />}
                          </Box>
                        ))}
                      </Box>
                    ))}

                    {siteIndex <
                      Object.keys(groupedBySiteAndDay).length - 1 && (
                      <Divider sx={{ borderBottomWidth: 2 }} />
                    )}
                  </Box>
                );
              })}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: theme.shadows[2],
            "&:hover": {
              boxShadow: theme.shadows[4],
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {t("documentsPage.closeButton") || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SimopsDialog;
